import { TranslateService } from '@ngx-translate/core';
import { CookieCategory, CookieConsentAgreement, CookieConsentPopupLayoutEnum, CookieConsentPopupPositionEnum, CookieConsentProviderType, LegalDocumentModel, PaymentModeEnum, ProviderType, StorageKeys } from '../../models';
import { CookieConsentProvider } from './cookie-consent-provider';
import { EmbedVideoUsageService } from '../embed-video-usage-api.service';
import { STORAGE_KEYS } from '../../constants';
import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject } from 'rxjs';

declare var initCookieConsent: any;

@Injectable()
export class DefaultCookieConsentProvider extends CookieConsentProvider {
  public readonly type = CookieConsentProviderType.DEFAULT;

  public initialized = new BehaviorSubject(true);

  public cookieConsentAgreement: BehaviorSubject<CookieConsentAgreement | null> = new BehaviorSubject<CookieConsentAgreement | null>(null);

  private readonly revision = 1.3;

  private cookieConsent: any;

  constructor(
    private readonly embedVideoUsageService: EmbedVideoUsageService,
    private readonly translateService: TranslateService,
    @Inject(STORAGE_KEYS) private readonly storageKeys: StorageKeys
  ) {
    super();
  }

  public togglePreferencesSettings(): void {
    this.cookieConsent.showSettings();
  }

  public async openCookieConsentDialog(settings: any, documents: LegalDocumentModel[] = []): Promise<any> {
    if (!initCookieConsent) {
      return;
    }

    const dialogTitle = await this.translateService.get('COOKIES.cookies_policy').toPromise();
    this.cookieConsent = initCookieConsent();

    const documentsLinks = documents.map(item => {
      return `<a href="/document/${item.groupId}" target="_blank" class="cc-link">${item.title}</a>`;
    }).join(', ');

    const positionMap = {
      [CookieConsentPopupPositionEnum.TOP_LEFT]: 'top left',
      [CookieConsentPopupPositionEnum.TOP_CENTER]: 'top center',
      [CookieConsentPopupPositionEnum.TOP_RIGHT]: 'top right',
      [CookieConsentPopupPositionEnum.CENTER_LEFT]: 'middle left',
      [CookieConsentPopupPositionEnum.CENTER]: 'middle center',
      [CookieConsentPopupPositionEnum.CENTER_RIGHT]: 'middle right',
      [CookieConsentPopupPositionEnum.BOTTOM_LEFT]: 'bottom left',
      [CookieConsentPopupPositionEnum.BOTTOM_CENTER]: 'bottom center',
      [CookieConsentPopupPositionEnum.BOTTOM_RIGHT]: 'bottom right',
    };

    const layout = settings.cookieConsent?.layout || CookieConsentPopupLayoutEnum.BOX;
    const position = positionMap[settings.cookieConsent?.position] || positionMap[CookieConsentPopupPositionEnum.BOTTOM_RIGHT]
    this.cookieConsent.run({
      current_lang: this.translateService.currentLang,
      cookie_name: this.storageKeys.COOKIE_CONSENT,
      cookie_expiration: 180,
      revision: this.revision,
      force_consent: settings.cookieConsent?.forceConsent,
      autoclear_cookies: true,
      gui_options: {
        consent_modal: {
          layout,
          position: layout === CookieConsentPopupLayoutEnum.BAR ? CookieConsentPopupPositionEnum.BOTTOM_CENTER : position,
        }
      },
      onFirstAction: (_: any, cookieConsentAgreement: CookieConsentAgreement) => {
        this.cookieConsentAgreement.next(cookieConsentAgreement);
      },
      onAccept: (cookieConsentAgreement: CookieConsentAgreement) => {
        this.cookieConsentAgreement.next(cookieConsentAgreement);
      },
      languages: {
        [this.translateService.currentLang]: {
          consent_modal: {
            title: dialogTitle,
            description: `
            ${this.translateService.instant('COOKIE_CONSENT.website_uses_essential_cookies')}
            <button type="button" data-cc="c-settings" class="cc-link">${this.translateService.instant('COOKIS_CONSENT.let_me_choose')}</button>`,
            primary_btn: {
              text: this.translateService.instant('COOKIE_CONSENT.accept_all'),
              role: 'accept_all'
            },
            secondary_btn: {
              text: this.translateService.instant('COOKIE_CONSENT.settings'),
              role: 'settings'
            }
          },
          settings_modal: {
            title: this.translateService.instant('COOKIE_CONSENT.cookie_preferences'),
            save_settings_btn: this.translateService.instant('COOKIE_CONSENT.save_settings'),
            accept_all_btn: this.translateService.instant('COOKIE_CONSENT.accept_all'),
            close_btn_label: this.translateService.instant('COOKIE_CONSENT.close'),
            reject_all_btn: this.translateService.instant('COOKIE_CONSENT.deny_all'),

            cookie_table_headers: [
              { name: this.translateService.instant('COOKIE_CONSENT.name') },
              { domain: this.translateService.instant('COOKIE_CONSENT.domain') },
              { type: this.translateService.instant('COOKIE_CONSENT.type') },
              { exp: this.translateService.instant('COOKIE_CONSENT.expiration') },
              { description: this.translateService.instant('COOKIE_CONSENT.description') }
            ],
            blocks: [
              {
                title: this.translateService.instant('COOKIE_CONSENT.cookie_usage'),
                description: `
                ${this.translateService.instant('COOKIE_CONSENT.we_user_cookies_to_ensure')}
                ${documentsLinks.length ? this.translateService.instant('COOKIE_CONSENT.for_more_details_relative') : ''}
                ${documentsLinks}`
              }, {
                title: this.translateService.instant('COOKIE_CONSENT.strictly_necessary_cookies'),
                description: this.translateService.instant('COOKIE_CONSENT.strictly_necessary_cookies_description'),
                toggle: {
                  value: CookieCategory.NECCESSARY,
                  enabled: true,
                  readonly: true
                },
                cookie_table: [
                  ...this.getNeccessaryCookies(settings)
                ]
              }, {
                title: this.translateService.instant('COOKIE_CONSENT.anylytics_data'),
                description: this.translateService.instant('COOKIE_CONSENT.analytics_data_description'),
                toggle: {
                  value: CookieCategory.PERFORMANCE,
                  enabled: false,
                  readonly: false
                },
                cookie_table: [
                  ...await this.getAnalyticsCookies(settings)
                ]
              }
            ]
          }
        }
      }
    });
  }

  private getNeccessaryCookies(settings: any): { name: string, domain: string, exp: string, description: string, type: string }[] {
    return [
      {
        name: this.storageKeys.REFRESH_TOKEN,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.session-key'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.LOGGED_IN_USER,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.logged_user_identificator'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.COOKIE_CONSENT,
        exp: this.translateService.instant('COMMON.days-short-format', { days: 180 }),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.cookie_consent_key'),
        type: this.translateService.instant('COOKIE_CONSENT.cookie')
      },
      {
        name: this.storageKeys.CLASS_LAYOUT,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.class_layout_key'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.PLATFORM_LANGUAGE,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.platform_language_key'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.SCORM_SUSPEND_DATA,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.scorm_suspend_data'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.LIBRARY_LIST_LAYOUT,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.library_layout_key'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.BRANCH_NAME,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.branch_name'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.OPENED_LESSON_GROUPS,
        exp: this.translateService.instant('COOKIE_CONSENT.session'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.opened_lesson_groups'),
        type: this.translateService.instant('COOKIE_CONSENT.session_storage')
      },
      {
        name: this.storageKeys.USER_SPENT_TIME,
        exp: this.translateService.instant('COOKIE_CONSENT.session'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.user_spent_time'),
        type: this.translateService.instant('COOKIE_CONSENT.session_storage')
      },
      ...this.getDownloadForOfflineCookies(settings),
      ...this.getPaymentlCookies(settings),
      {
        name: 'pdfjs.history',
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.pdfjs_history'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
    ]
  }

  private async getAnalyticsCookies(settings: any): Promise<{ name: string, domain: string, exp: string, description: string, type: string }[]> {
    const analyticsCookies = [
      {
        name: 'cache-sprite-plyr',
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.cookie_necessary_for_cache_function'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      }
    ];

    const youtubeVideoUsage = await this.embedVideoUsageService.getVideoProviderUsage(ProviderType.YOUTUBE).toPromise();

    if (youtubeVideoUsage?.isUsing) {
      analyticsCookies.unshift({
        name: 'PREF',
        domain: 'youtube.com',
        exp: this.translateService.instant('COMMON.days-short-format', { days: 639 }),
        description: this.translateService.instant('COOKIE_CONSENT.recording_your_preferences_and_other_information'),
        type: this.translateService.instant('COOKIE_CONSENT.cookie')
      },
      {
        name: 'GPS',
        domain: 'youtube.com',
        exp: this.translateService.instant('COMMON.hours_full_format', { hours: 1 }),
        description: this.translateService.instant('COOKIE_CONSENT.detecting_unique_id'),
        type: this.translateService.instant('COOKIE_CONSENT.cookie')
      },
      {
        name: 'VISITOR_INFO1_LIVE',
        domain: 'youtube.com',
        exp: this.translateService.instant('COMMON.days-short-format', { days: 90 }),
        description: this.translateService.instant('COOKIE_CONSENT.cookie_used_as_unique_identifier'),
        type: this.translateService.instant('COOKIE_CONSENT.cookie')
      },
      {
        name: 'YSC',
        domain: 'youtube.com',
        exp: this.translateService.instant('COOKIE_CONSENT.session'),
        description: this.translateService.instant('COOKIE_CONSENT.tracking_view_embedded_videos'),
        type: this.translateService.instant('COOKIE_CONSENT.session_storage')
      });
    }

    const vimeoVideoUsage = await this.embedVideoUsageService.getVideoProviderUsage(ProviderType.VIMEO).toPromise();

    if (vimeoVideoUsage?.isUsing) {
      analyticsCookies.unshift({
        name: 'sync_active',
        domain: 'player.vimeo.com',
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        description: this.translateService.instant('COOKIE_CONSENT.contains_data_on_visitors_video'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      });
    }

    if (settings.googleAnalytics?.tagManagerId || settings.googleAnalytics?.trackingId) {
      analyticsCookies.unshift({
        name: '^_ga',
        domain: 'google.com',
        exp: this.translateService.instant('COMMON.year-long-format', { years: 2 }),
        description: this.translateService.instant('COOKIE_CONSENT.used_to_distinguish_users'),
        type: this.translateService.instant('COOKIE_CONSENT.cookie')
      },
      {
        name: '^_ga_[container-id]',
        domain: 'google.com',
        exp: this.translateService.instant('COMMON.year-long-format', { years: 2 }),
        description: this.translateService.instant('COOKIE_CONSENT.used_to_persist_session_state'),
        type: this.translateService.instant('COOKIE_CONSENT.cookie')
      })
    }

    return analyticsCookies;
  }

  private getPaymentlCookies(settings: any): { name: string, exp: string, domain: string, description: string, type: string }[] {
    if (!settings) {
      return [];
    }

    const additional = [];

    const stripeEnabled = settings.payment.mode === PaymentModeEnum.STRIPE;

    if (stripeEnabled) {
      additional.push({
        name: '__stripe_sid',
        exp: this.translateService.instant('COMMON.days-short-format', { days: 365 }),
        domain: '.stripe.com',
        description: this.translateService.instant('COOKIE_CONSENT.used_provide_fraud_prevention'),
        type: this.translateService.instant('COOKIE_CONSENT.cookie')
      },
        {
          name: '__stripe_mid',
          exp: this.translateService.instant('COMMON.days-short-format', { days: 365 }),
          domain: '.stripe.com',
          description: this.translateService.instant('COOKIE_CONSENT.used_provide_fraud_prevention'),
          type: this.translateService.instant('COOKIE_CONSENT.cookie')
        },
        {
          name: 'recent-views',
          exp: this.translateService.instant('COMMON.days-short-format', { days: 365 }),
          domain: '.stripe.com',
          description: this.translateService.instant('COOKIE_CONSENT.used_to_save_your_preferences'),
          type: this.translateService.instant('COOKIE_CONSENT.cookie')
        });
    }


    return additional;
  }

  private getDownloadForOfflineCookies(settings: any): { name: string, exp: string, domain: string, description: string, type: string }[] {
    if (!settings) {
      return [];
    }

    const downloadEnabled = settings.mobileApp?.downloadForOfflineEnabled;

    if (!downloadEnabled) {
      return [];
    }

    return [
      {
        name: this.storageKeys.OFFLINE_USER_DATA,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.offline_user_data'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.OFFLINE_CLASSES,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.offline_classes'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.OFFLINE_COURSES,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.offline_courses'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
      {
        name: this.storageKeys.PENDING_REQUESTS,
        exp: this.translateService.instant('COOKIE_CONSENT.no_expiration'),
        domain: `${window.location.protocol}//${window.location.host}`,
        description: this.translateService.instant('COOKIE_CONSENT.pending_requests'),
        type: this.translateService.instant('COOKIE_CONSENT.local_storage')
      },
    ]
  }
}
